import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { Divider, Grid, Heading, Stack, Text } from '@eneco/sparky';
import type { ResponseModels_Products_Interruption_InterruptionModel } from '@monorepo-types/dc';

import type { WarmthInterruptionsContent } from './warmth-interruptions-content.types';

type Props = {
  content: WarmthInterruptionsContent;
  interruptions: ResponseModels_Products_Interruption_InterruptionModel['data'];
  toRichText: (raw: unknown) => string | undefined;
};

/**
 * The list inside both of `WarmthInterruptions`' dialogs. Mirrors
 * `libs/sitecore/components/content/src/WarmthInterruptions/WarmthInterruptionsDialog.tsx` without its
 * Experience Editor `wrap()` calls, which outside the editor return the plain field values.
 */
export const WarmthInterruptionsDialog: FC<Props> = ({ content, interruptions, toRichText }) => {
  const { date } = useFormatter();

  if (!interruptions?.length) {
    return <RichText html={toRichText(content.noInterruptionsContent)} />;
  }

  const formatDate = (datestring?: string | null) => {
    if (datestring) {
      const time = datestring.includes('T') ? ` / ${date.hourLong(new Date(datestring))}` : '';
      return `${date.medium(new Date(datestring))}${time}`;
    }
  };

  return (
    <Stack gap="6">
      {interruptions.map((item, index) => (
        <Stack gap="6" key={index}>
          <Stack gap="2">
            <Heading size="XS" as="h3">
              {item.address?.city}
            </Heading>
            <Heading size="3XS" as="h4" color="textLowEmphasis">
              {`${content.areaLabel} ${item.address?.postalCode} ${item.address?.street}`}
            </Heading>
          </Stack>
          <Grid gridTemplateColumns="1fr 1fr" gap="2">
            <Grid.Item>
              <Text weight="bold">{content.startDateLabel}</Text>
            </Grid.Item>
            <Grid.Item>{formatDate(item.startDate)}</Grid.Item>
            <Grid.Item>
              <Text weight="bold">{content.endDateLabel}</Text>
            </Grid.Item>
            <Grid.Item>{formatDate(item.expectedEndDate)}</Grid.Item>
            <Grid.Item>
              <Text weight="bold">{content.effectLabel}</Text>
            </Grid.Item>
            <Grid.Item>{item.effect}</Grid.Item>
            <Grid.Item>
              <Text weight="bold">{content.typeLabel}</Text>
            </Grid.Item>
            <Grid.Item>{item.isPlanned ? content.plannedText : content.unplannedText}</Grid.Item>
          </Grid>
          <Divider />
        </Stack>
      ))}
      <RichText html={toRichText(content.additionalInformationContent)} />
      <Divider />
      <Text size="BodyS" color="textLowEmphasis">
        <RichText html={toRichText(content.disclaimerContent)} />
      </Text>
    </Stack>
  );
};
